import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { notificationInboxApi } from '@/api/modules/notification-inbox';
import { reminderApi } from '@/api/modules/reminder';
import { useAuthStore } from '@/stores/auth';

const REFRESH_INTERVAL_MS = 60 * 1000;

export function useNotificationCounts() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const {
    data: messageData,
    isFetching: isMessageFetching,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: ['notification-inbox', 'unread-count'],
    queryFn: () => notificationInboxApi.unreadCount(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  const {
    data: reminderData,
    isFetching: isReminderFetching,
    refetch: refetchReminders,
  } = useQuery({
    queryKey: ['reminder', 'pending-count'],
    queryFn: () => reminderApi.pendingCount(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const refetchVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void refetchMessages();
      void refetchReminders();
    };

    const intervalId = window.setInterval(refetchVisible, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [refetchMessages, refetchReminders]);

  const messageUnreadCount = messageData?.unread_count ?? 0;
  const reminderPendingCount = reminderData?.pending_count ?? 0;

  return {
    messageUnreadCount,
    reminderPendingCount,
    totalCount: messageUnreadCount + reminderPendingCount,
    isFetching: isMessageFetching || isReminderFetching,
  };
}
